/**
 * Phone verification before the courier review step.
 *
 * The courier cannot open review until the mobile number from the personal
 * step answers an SMS challenge. `verifyCourierRegistrationOtp` returns a
 * short-lived token that the final submit must carry.
 */

import { apiClient } from 'shared/lib/apiClient';
import type { SupportedLanguage } from 'shared/i18n/i18n';

export interface SendOtpResult {
  challengeId: string;
  otpExpiresIn: number;
  sentTo: string;
}

export interface VerifyOtpResult {
  ok: boolean;
  verificationToken: string;
}

export type OtpErrorCode =
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'OTP_LOCKED'
  | 'OTP_RESEND_COOLDOWN'
  | 'OTP_NOT_FOUND'
  | 'OTP_SEND_FAILED'
  | 'UNKNOWN';

export const otpErrorCode = (err: unknown): OtpErrorCode => {
  const code = (err as { response?: { data?: { code?: string } } })?.response
    ?.data?.code;
  const known: OtpErrorCode[] = [
    'OTP_INVALID',
    'OTP_EXPIRED',
    'OTP_LOCKED',
    'OTP_RESEND_COOLDOWN',
    'OTP_NOT_FOUND',
    'OTP_SEND_FAILED',
  ];
  return known.find((k) => k === code) ?? 'UNKNOWN';
};

export async function sendCourierRegistrationOtp(
  phoneNumber: string,
  language: SupportedLanguage,
): Promise<SendOtpResult> {
  const { data } = await apiClient.post<SendOtpResult>(
    '/api/courier-onboarding/otp/send',
    { phoneNumber, language },
  );
  return data;
}

export async function resendCourierRegistrationOtp(
  challengeId: string,
): Promise<SendOtpResult> {
  const { data } = await apiClient.post<SendOtpResult>(
    '/api/courier-onboarding/otp/resend',
    { challengeId },
  );
  return data;
}

export async function verifyCourierRegistrationOtp(
  challengeId: string,
  code: string,
): Promise<VerifyOtpResult> {
  const { data } = await apiClient.post<VerifyOtpResult>(
    '/api/courier-onboarding/otp/verify',
    { challengeId, code },
  );
  return data;
}
